import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IQueue } from '../printer/models/queue.model';
import { IUser } from '../user/models/user.model';
import { PagingService } from '../paging/paging.service';
import { PrinterService } from '../printer/printer.service';
import { UserService } from '../user/user.service';
import { ErrorService } from '../error/error.service';
import { Logger, LoggingService } from 'ionic-logging-service';

@Injectable({
  providedIn: 'root'
})
export class PrinterQueueCleanupService {
  private logger: Logger;

  constructor(
    private pagingService: PagingService,
    private printerService: PrinterService,
    private userService: UserService,
    private errorService: ErrorService,
    private loggingService: LoggingService
  ) {
    this.logger = this.loggingService.getLogger('[PrinterQueueCleanupService]');
  }

  /**
   * Removes pinned printers from user settings (legacy cleanup)
   */
  async removePinnedPrinters(pinnedPrinterUrls: string[]): Promise<void> {
    this.logger.info('removePinnedPrinters() pinnedPrinterUrls.length: ' + pinnedPrinterUrls.length);
    
    try {
      await firstValueFrom(
        this.userService.changePrinterPinStatus(pinnedPrinterUrls, false)
      );
      this.logger.info('Successfully removed pinned printers');
    } catch (error) {
      this.logger.error('Error removing pinned printers', error);
      throw error;
    }
  }

  /**
   * Removes unavailable printer queues from user's favorite queues
   * Returns updated list of available queue URLs
   */
  async removeUnavailablePrinterQueues(
    favoriteQueueUrls: string[],
    user: IUser
  ): Promise<string[]> {
    this.logger.info('removeUnavailablePrinterQueues() favoriteQueuesCount: ' + favoriteQueueUrls.length);
    
    try {
      // Get list of available queues from the provided favorites
      const queueParameters = this.pagingService.getQueueParameters(
        0, 
        500, 
        [], 
        null, 
        favoriteQueueUrls
      );
      queueParameters.printerTypes = ['NETWORK', 'VIRTUAL'];
      
      const queues = await firstValueFrom(
        this.printerService.getPrinterQueuesList(queueParameters)
      );

      // Remove metadata entries
      queues.pop(); // page
      queues.pop(); // printers

      let updatedFavoriteQueues: string[];

      if (queues.length === 0) {
        // No queues available - reset favorites to empty array
        this.logger.info('No available queues found, resetting favorites');
        updatedFavoriteQueues = [];
      } else {
        // Extract only the available queue URLs
        updatedFavoriteQueues = queues.map((q: IQueue) => q.links.self);
        this.logger.info('Found ' + updatedFavoriteQueues.length + ' available queues');
      }

      // Update user settings with available queues
      await firstValueFrom(
        this.userService.setAvailablePinnedPrinterQueues(updatedFavoriteQueues)
      );

      // Refresh user to get updated settings
      await firstValueFrom(
        this.userService.refreshCurrentUser()
      );

      // Return the updated list from the refreshed user
      const refreshedUser = this.userService.userSignal();
      return refreshedUser.userAppSettings.pinnedPrinterQueues;

    } catch (error) {
      this.logger.error('Error removing unavailable printer queues', error);
      
      // Handle HTTP errors gracefully
      const result = await firstValueFrom(
        this.errorService.handleHttpClientResponseError(
          error, 
          'POST', 
          'removeUnavailablePrinterQueues()'
        )
      );

      if (result === 'unauthenticated') {
        this.logger.info('User unauthenticated');
      }

      // Return original list on error
      return favoriteQueueUrls;
    }
  }
}
